import { useEffect, useRef } from "react"
import { Box, Button, IconButton, MenuItem, Select, Stack, Tab, Tabs, Tooltip, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { Bound } from "lib/@components/binding/Bound"
import { If, Else, Switch, Case } from "lib/@components/switch"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useReferenceState } from "lib/@hooks/use-reference"
import { confirm } from "lib/@dialogs/confirm"
import { useDialog } from "lib/@hooks/useDialog"
import { NameDialog } from "lib/@dialogs/nameDialog"
import { invalidate } from "lib/graphql/cache"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { getFentraProfiles } from "routes/qa/fentra/controllers/get-fentra-profiles"
import {
    createFentraProfile,
    renameFentraProfile,
    deleteFentraProfile,
} from "routes/qa/fentra/controllers/fentra-mutations"
import { FentraConfigTab } from "routes/qa/fentra/config/fentra-config-tab"
import { FentraUsersTab } from "routes/qa/fentra/users/fentra-users-tab"
import { FentraGroupsTab } from "routes/qa/fentra/groups/fentra-groups-tab"
import { FentraLogTab } from "routes/qa/fentra/log/fentra-log-tab"

// Every profile is live at once — this page only chooses which one you are looking at.
// There is no "active" profile: the mock IdP resolves profiles from the URL or the form
// body, never from a flag, so two SSO/SCIM configurations can run side by side.
export function FentraPage() {
    // Stable fallback: `?? []` allocates a new array every render, which makes it an unstable
    // dependency for the effects below — the very churn eslint warns about on this component.
    const noProfiles = useRef([])
    const profiles = getFentraProfiles.useResults() ?? noProfiles.current
    const [profileId, setProfileId] = useSearchParam("fp", "")
    const [tab, setTab] = useSearchParam("ft", "config")
    // SNAG2-001: which profile you were last working in, remembered per user. Landing on the
    // alphabetically first profile instead cross-wires tenants: you copy the IdP login URL and
    // certificate off whichever profile the page silently chose and paste them into the company
    // you meant to configure, and nothing about either value looks wrong afterwards.
    const [lastProfileId, setLastProfileId] = useReferenceState("fentra-last-profile", "")
    // House pattern for a single-line text prompt. Native window.prompt() blocks the
    // renderer, which makes the page undrivable by Cypress and by CDP automation.
    const getProfileName = useDialog(<NameDialog label="Profile name" />, { maxWidth: "xs", fullWidth: true })

    // With no `fp` in the URL, reopen on the profile this user last had selected, falling back to
    // the first only when there is nothing to remember. No profile is privileged — the choice
    // still lives in the URL and affects nothing but this view.
    useEffect(selectDefaultProfile, [profiles, profileId, setProfileId, lastProfileId])

    const selectedProfile = profiles.find(matchesProfileId) ?? null

    // Remember whatever ends up selected, however it got there — a pasted `?fp=` link counts just
    // as much as using the dropdown. Only a profile that actually resolved is stored, so a stale
    // or deleted id never gets written back.
    useEffect(rememberSelectedProfile, [selectedProfile, setLastProfileId])
    const canDelete = profiles.length > 1
    // MenuItem elements for the Select — computed before JSX so we don't call .map() inline
    const profileMenuItems = profiles.map(toProfileMenuItem)
    const profileNames = profiles.map(toProfileName)

    return (
        <Box>
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ px: 3, py: 2, borderBottom: 1, borderColor: "divider" }}
            >
                <Typography variant="h6" sx={{ mr: 1 }}>
                    Fentra
                </Typography>

                <If value={profiles.length > 0}>
                    <Select
                        value={profileId}
                        onChange={handleProfileChange}
                        size="small"
                        sx={{ minWidth: 200 }}
                        displayEmpty
                    >
                        {profileMenuItems}
                        <MenuItem value="__add__">
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Iconify icon="mdi:plus" />
                                <span>Add Profile</span>
                            </Stack>
                        </MenuItem>
                    </Select>
                </If>

                <If value={!!selectedProfile}>
                    <Tooltip title="Rename profile">
                        <IconButton size="small" onClick={handleRename}>
                            <Iconify icon="mdi:pencil" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={canDelete ? "Delete profile" : "Cannot delete the only profile"}>
                        <span>
                            <IconButton
                                aria-label="Delete profile"
                                size="small"
                                onClick={handleDelete}
                                disabled={!canDelete}
                            >
                                <Iconify icon="mdi:delete" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </If>

                <Box sx={{ flexGrow: 1 }} />

                <If value={profiles.length > 1}>
                    <Typography variant="caption" color="text.secondary">
                        All {profiles.length} profiles are live — each has its own keys and URLs
                    </Typography>
                </If>
            </Stack>

            <If value={!!selectedProfile}>
                <Bound target={selectedProfile}>
                    <Tabs
                        value={tab}
                        onChange={handleTabChange}
                        sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}
                    >
                        <Tab value="config" label="Configuration" />
                        <Tab value="users" label="Users" />
                        <Tab value="groups" label="Groups" />
                        <Tab value="activity" label="Activity" />
                    </Tabs>

                    <Switch value={tab}>
                        <Case when="config">
                            <FentraConfigTab key={selectedProfile?._id} />
                        </Case>
                        <Case when="users">
                            <FentraUsersTab key={selectedProfile?._id} />
                        </Case>
                        <Case when="groups">
                            <FentraGroupsTab key={selectedProfile?._id} />
                        </Case>
                        <Case when="activity">
                            <FentraLogTab key={selectedProfile?._id} />
                        </Case>
                    </Switch>
                </Bound>
                <Else>
                    <Box sx={{ p: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            No profiles yet.{" "}
                            <Button variant="text" onClick={handleAddProfile}>
                                Create a profile
                            </Button>{" "}
                            to get started.
                        </Typography>
                    </Box>
                </Else>
            </If>
        </Box>
    )

    function selectDefaultProfile() {
        if (profileId || profiles.length === 0) return
        // The remembered profile may have been renamed (fine, id is stable) or deleted (not fine) —
        // resolve it against the current list rather than trusting it, or a deleted profile would
        // leave the page on nothing.
        const remembered = profiles.find(matchesLastProfileId)
        setProfileId(remembered?._id ?? profiles[0]._id)
    }

    function rememberSelectedProfile() {
        if (selectedProfile?._id && selectedProfile._id !== lastProfileId) {
            setLastProfileId(selectedProfile._id)
        }
    }

    function matchesProfileId(p) {
        return p._id === profileId
    }

    function matchesLastProfileId(p) {
        return p._id === lastProfileId
    }

    function toProfileMenuItem(p) {
        return (
            <MenuItem key={p._id} value={p._id}>
                {p.name}
            </MenuItem>
        )
    }

    function toProfileName(p) {
        return p.name
    }

    function isOtherProfileName(name) {
        return name !== selectedProfile?.name
    }

    function handleTabChange(_, v) {
        setTab(v)
    }

    async function handleProfileChange(e) {
        if (e.target.value === "__add__") {
            await handleAddProfile()
        } else {
            setProfileId(e.target.value)
        }
    }

    async function handleAddProfile() {
        const name = await getProfileName({ title: "New profile", namesList: profileNames })
        if (!name) return
        try {
            const created = await createFentraProfile({ name })
            invalidate("fentraProfiles")
            setProfileId(created._id)
        } catch (err) {
            errorSnackbar(err?.message || "Failed to create profile")
        }
    }

    async function handleRename() {
        if (!selectedProfile) return
        // Exclude the current name so reopening the dialog on an unchanged name is not
        // reported as a duplicate of itself.
        const name = await getProfileName({
            title: "Rename profile",
            value: selectedProfile.name,
            namesList: profileNames.filter(isOtherProfileName),
        })
        if (!name || name === selectedProfile.name) return
        try {
            await renameFentraProfile({ id: selectedProfile._id, name })
            invalidate("fentraProfiles")
        } catch (err) {
            errorSnackbar(err?.message || "Failed to rename profile")
        }
    }

    async function handleDelete() {
        if (!selectedProfile || !canDelete) return
        if (!(await confirm(`Delete profile "${selectedProfile.name}"? This cannot be undone.`, "Delete profile")))
            return
        try {
            await deleteFentraProfile({ id: selectedProfile._id })
            invalidate("fentraProfiles")
            // Select the first remaining profile
            setProfileId(profiles.filter((p) => p._id !== selectedProfile._id)[0]?._id ?? "")
            successSnackbar("Profile deleted")
        } catch (err) {
            errorSnackbar(err?.message || "Failed to delete profile")
        }
    }
}
